import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Mag } from '../components/mag.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import type { ActivitySummary, MeResponse, TrustSummary } from '../lib/api.js';
import { formatNumber, formatRelativeTimeCompact } from '../lib/format.js';
import { activityQuery, meQuery, trustQuery } from '../lib/queries.js';

// Mag's activity ledger — every LLM call she's made for this org, rolled
// up by role + cost. Founders are curious; ops needs the visibility. The
// table reads llm_calls directly, so the same data feeds future
// audit / billing flows.

export const Route = createFileRoute('/activity')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const [summary, trust] = await Promise.all([
      queryClient.ensureQueryData(activityQuery(30)),
      queryClient.ensureQueryData(trustQuery(30)),
    ]);
    return { me, summary, trust };
  },
  loader: ({ context }) =>
    context as {
      me: MeResponse;
      summary: ActivitySummary | null;
      trust: TrustSummary | null;
    },
  component: ActivityPage,
});

// Display labels in lowercase Mag-voice. Roles that haven't been wired yet
// still render — just the role name in mono.
const ROLE_LABEL: Record<string, string> = {
  orchestrator: 'orchestrator · opus',
  concept: 'concept · sonnet',
  critic: 'critic · sonnet',
  generation: 'generation · haiku',
  performance: 'performance · haiku',
  routine: 'routine · haiku',
};

function formatCost(usd: string): string {
  const n = Number(usd);
  if (!Number.isFinite(n)) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/** Was a third hand-rolled English calendar ("just now", "5m ago").
 *  formatRelativeTimeCompact is the wave-2 helper that does this from
 *  Intl, including the localized "now" below its threshold. */
function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  return formatRelativeTimeCompact(iso);
}

const KIND_LABEL: Record<string, MessageDescriptor> = {
  concept_proposal: msg`concepts`,
  creative_variants: msg`hooks`,
};

const kindLabel = (kind: string): string => {
  const label = KIND_LABEL[kind];
  return label ? i18n._(label) : kind;
};

function formatScore(score: number | null): string {
  if (score === null) return '—';
  return `${Math.round(score * 100)}%`;
}

function ActivityPage() {
  const { me, summary, trust } = Route.useLoaderData();

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav active="activity" />

      <section className="mx-auto max-w-[820px] px-4 py-12 sm:px-6">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>mag's activity</Trans>
        </p>
        <h1 className="mt-3 font-display text-[38px] sm:text-[56px] font-black leading-[1] tracking-[-0.04em]">
          <Trans>what mag's been up to.</Trans>
        </h1>
        <p className="mt-3 text-[15px] leading-[1.6] text-[var(--ink)]/70">
          <Trans>
            every model call she's made for{' '}
            {me.name.split(' ')[0]?.toLowerCase() ?? i18n._(msg`you`)}, last{' '}
            {summary?.windowDays ?? 30} days. costs round to the nearest cent over a penny.
          </Trans>
        </p>

        <div className="mt-8 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)]">
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>totals</Trans>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
              <Trans>{summary?.windowDays ?? 30} days</Trans>
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <Stat label={i18n._(msg`calls`)} value={String(summary?.totals.calls ?? 0)} />
            <Stat label={i18n._(msg`cost`)} value={formatCost(summary?.totals.costUsd ?? '0')} />
            {/* s184 — surface the WS1 caching headline. The plan's "spirit"
                section calls cache hit rate the leading indicator the
                founder cares about. We render it whenever the window has
                any traffic; 0% on a cold start is a real answer. */}
            {summary && summary.totals.calls > 0 ? (
              <Stat
                label={i18n._(msg`cache hit`)}
                value={`${Math.round(summary.totals.cacheHitRate * 100)}%`}
              />
            ) : null}
          </div>
        </div>

        <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
          <Trans>mag's score</Trans>
        </h2>
        <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
          <Trans>accept rate per card kind. mag will earn more autonomy as this climbs.</Trans>
        </p>
        {trust ? (
          <div className="mt-3 rounded-2xl border border-[var(--line)] bg-white p-5">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                {'↘'} <Trans>overall</Trans>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                <Trans>{trust.windowDays} days</Trans>
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-3">
              <Stat label={i18n._(msg`kept`)} value={String(trust.overall.kept)} />
              <Stat label={i18n._(msg`skipped`)} value={String(trust.overall.skipped)} />
              <Stat label={i18n._(msg`accept rate`)} value={formatScore(trust.overall.score)} />
            </div>
            {trust.byKind.some((k) => k.kept + k.skipped > 0) ? (
              <div className="mt-5 flex flex-col gap-3">
                {trust.byKind.map((k) => (
                  <div
                    key={k.kind}
                    className="flex items-baseline justify-between rounded-lg border border-[var(--line)] bg-[var(--paper)] px-4 py-3"
                  >
                    <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                      {kindLabel(k.kind)}
                    </span>
                    <div className="flex items-baseline gap-6">
                      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/60">
                        <Trans>
                          kept {k.kept} · skipped {k.skipped} · open {k.open}
                        </Trans>
                      </span>
                      <span className="font-display text-[18px] font-bold tracking-[-0.015em]">
                        {formatScore(k.score)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                {'↘'} <Trans>no verdicts yet — accept or skip a card and the score fills in.</Trans>
              </p>
            )}
          </div>
        ) : null}

        {summary?.byBrand && summary.byBrand.length > 1 ? (
          <>
            <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
              <Trans>by brand</Trans>
            </h2>
            <div className="mt-3 flex flex-col gap-3">
              {summary.byBrand.map((b) => (
                <article
                  key={b.brandId}
                  className="rounded-xl border border-[var(--line)] bg-white p-5"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                      {b.brandName}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                      {b.calls} <Plural value={b.calls} one="call" other="calls" />
                    </span>
                  </div>
                  <div className="mt-2 flex items-baseline gap-x-8">
                    <Stat label={i18n._(msg`cost`)} value={formatCost(b.costUsd)} />
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : null}

        {summary?.tools.byTool && summary.tools.byTool.length > 0 ? (
          <>
            <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
              <Trans>tools mag used</Trans>
            </h2>
            <p className="mt-1 text-[13px] leading-[1.5] text-[var(--ink)]/65">
              <Trans>
                every analytics tool call mag fired in the last {summary.tools.windowDays} days,
                sorted by frequency. errors flag a tool the model is calling wrong (or that the
                executor rejected).
              </Trans>
            </p>
            <div className="mt-3 flex flex-col gap-3">
              {summary.tools.byTool.map((t) => (
                <article
                  key={t.toolName}
                  className="rounded-xl border border-[var(--line)] bg-white p-5"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                      {t.toolName}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                      <Trans>last call · {formatRelative(t.lastAt)}</Trans>
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-3">
                    <Stat label={i18n._(msg`calls`)} value={formatNumber(t.calls)} />
                    <Stat label={i18n._(msg`avg latency`)} value={`${t.avgDurationMs}ms`} />
                    {t.errors > 0 ? (
                      <Stat label={i18n._(msg`errors`)} value={formatNumber(t.errors)} />
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : null}

        <h2 className="mt-10 font-display text-[20px] font-bold tracking-[-0.02em]">
          <Trans>by role</Trans>
        </h2>
        {summary?.byRole.length ? (
          <div className="mt-3 flex flex-col gap-3">
            {summary.byRole.map((r) => (
              <article key={r.role} className="rounded-xl border border-[var(--line)] bg-white p-5">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]">
                    {ROLE_LABEL[r.role] ?? r.role}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                    <Trans>last call · {formatRelative(r.lastAt)}</Trans>
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-3">
                  <Stat label={i18n._(msg`calls`)} value={String(r.calls)} />
                  <Stat label={i18n._(msg`cost`)} value={formatCost(r.costUsd)} />
                  <Stat
                    label={i18n._(msg`tokens (in/out)`)}
                    value={`${formatNumber(r.totalInputTokens)} / ${formatNumber(r.totalOutputTokens)}`}
                  />
                  {r.totalCachedTokens > 0 ? (
                    <Stat
                      label={i18n._(msg`cache hit`)}
                      value={`${Math.round(r.cacheHitRate * 100)}% · ${formatNumber(r.totalCachedTokens)} cached`}
                    />
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--paper)] p-8 text-center">
            <Mag size={56} mood="napping" />
            <p className="mt-3 font-serif text-lg leading-tight text-[var(--ink)]/80">
              <Trans>“quiet so far. ask me something and we'll fill this in.”</Trans>
            </p>
          </div>
        )}

        {/* s162.30 — "↘ back to the feed" trailer removed.
            WorkspaceNav now carries Brands/Activity/Billing tabs +
            the magig wordmark links back to /home, so the redundant
            text trailer just added clutter under the metrics table. */}
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {label}
      </span>
      <span className="font-display text-[20px] font-bold tracking-[-0.015em]">{value}</span>
    </div>
  );
}
